import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { getStoreProducts, purchaseProduct, restorePurchases, storeAvailable } from "@/lib/storekit";
import { Check, Loader2, Sparkles, SunMedium } from "lucide-react";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";

/**
 * PRICING / UPGRADE — Continua 1.1 subscription tiers.
 * Tier data (limits) comes from GET /api/subscription (`tiers`). On iOS the
 * purchase runs through the native StoreKit 2 bridge (real Apple payment
 * sheet, localized App Store pricing, backend-verified entitlement); on web
 * this screen is informational.
 */

const TIER_FEATURES = (t) => [
  t.monthly_sessions === null ? "Unlimited sessions" : `${t.monthly_sessions} sessions / month`,
  t.monthly_sessions === null ? "Extended session duration" : `Up to ${t.max_duration_minutes} minutes / session`,
  `${t.companions.length} characters`,
  `${t.atmospheres.length} atmospheres`,
  ...(t.monthly_morning_sessions > 0
    ? [`${t.monthly_morning_sessions} experimental Morning Sessions / month`]
    : []),
];

export default function Pricing() {
  const nav = useNavigate();
  const [sub, setSub] = useState(null);
  const [storeProducts, setStoreProducts] = useState({});
  const [busyProduct, setBusyProduct] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const purchasingRef = useRef(false);

  const refreshSub = () => api.get("/subscription").then((r) => setSub(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/subscription").then((r) => setSub(r.data)).catch(() => setSub(null));
    if (storeAvailable()) {
      getStoreProducts()
        .then((list) => {
          const map = {};
          list.forEach((p) => { map[p.productId] = p; });
          setStoreProducts(map);
        })
        .catch(() => setStoreProducts({}));
    }
  }, []);

  const currentTier = sub?.tier || "free";
  const tiers = sub?.tiers || [];

  const choose = async (t) => {
    if (t.id === currentTier) return;
    if (t.id === "free") {
      toast("To downgrade, manage your subscription in the App Store.");
      return;
    }
    if (!storeAvailable()) {
      toast(`${t.label} subscriptions are completed in the Continua iOS app through the App Store.`);
      return;
    }
    if (purchasingRef.current) return; // double-tap guard
    purchasingRef.current = true;
    setBusyProduct(t.product_id);
    try {
      const result = await purchaseProduct(t.product_id);
      if (result.status === "success") {
        if (result.subscription) setSub(result.subscription);
        else await refreshSub();
        toast.success(`${t.label} is active. Welcome.`);
      } else if (result.status === "pending") {
        toast("Your purchase is awaiting approval. Your plan will activate automatically once it completes.");
      } else if (result.status === "sync_failed") {
        toast("Payment received — we're confirming your plan. It will activate automatically shortly.");
      }
      // cancelled → silent, no error toast
    } catch (e) {
      toast.error(e?.message || "The purchase could not be completed. Please try again.");
    } finally {
      purchasingRef.current = false;
      setBusyProduct(null);
    }
  };

  const restore = async () => {
    if (!storeAvailable()) {
      toast("Restore Purchases runs in the Continua iOS app.");
      return;
    }
    if (restoring) return;
    setRestoring(true);
    try {
      const subscription = await restorePurchases();
      if (subscription) setSub(subscription);
      if (subscription?.is_paid) toast.success(`Restored: ${subscription.tier_label}.`);
      else toast("No active subscription found on this Apple ID.");
    } catch (e) {
      toast.error("Restore failed. Please try again.");
    } finally {
      setRestoring(false);
    }
  };

  return (
    <AppShell>
      <div className="max-w-md mx-auto px-6 pt-2 pb-16" data-testid="pricing-screen">
        <h1 className="font-serif-display text-5xl text-violet-50 mb-3 leading-none" data-testid="pricing-title">
          Plans
        </h1>
        <p className="text-sm text-violet-300/60 leading-relaxed mb-8">
          Every plan keeps both companions. Bigger plans open longer nights, more
          atmospheres, and the experimental mornings.
        </p>

        <div className="space-y-5">
          {tiers.map((t) => {
            const isCurrent = t.id === currentTier;
            const isPremiumTier = t.id === "premium";
            return (
              <section
                key={t.id}
                data-testid={`pricing-tier-${t.id}`}
                className={`relative rounded-2xl border px-5 py-5 transition-colors ${
                  isPremiumTier
                    ? "border-amber-200/35 bg-amber-100/[0.045]"
                    : "border-violet-200/15 bg-violet-200/[0.04]"
                }`}
              >
                {isPremiumTier && (
                  <span className="absolute -top-2.5 left-5 inline-flex items-center gap-1 rounded-full border border-amber-200/40 bg-[#141130] px-2.5 py-0.5 text-[9px] uppercase tracking-[0.24em] text-amber-200/90">
                    <Sparkles className="w-2.5 h-2.5" strokeWidth={1.5} /> Full experience
                  </span>
                )}
                <div className="flex items-baseline justify-between mb-1">
                  <h2 className={`font-serif-display text-xl ${isPremiumTier ? "text-amber-100/95" : "text-violet-50"}`}>
                    {t.label}
                  </h2>
                  <p className="text-violet-50 text-sm tabular-nums" data-testid={`pricing-price-${t.id}`}>
                    {storeProducts[t.product_id]?.displayPrice
                      ? storeProducts[t.product_id].displayPrice
                      : t.price_eur === 0 ? "€0" : `€${t.price_eur.toFixed(2)}`}
                    <span className="text-violet-300/50 text-xs"> / month</span>
                  </p>
                </div>
                <ul className="mt-3 space-y-1.5">
                  {TIER_FEATURES(t).map((f) => (
                    <li key={f} className="flex items-center gap-2 text-xs text-violet-300/70">
                      <Check className={`w-3 h-3 shrink-0 ${isPremiumTier ? "text-amber-200/70" : "text-violet-300/50"}`} strokeWidth={1.5} />
                      {f}
                    </li>
                  ))}
                </ul>
                {isPremiumTier && (
                  <p className="mt-3 flex items-start gap-1.5 text-[11px] text-amber-100/55 leading-relaxed" data-testid="pricing-morning-experimental-note">
                    <SunMedium className="w-3 h-3 mt-0.5 shrink-0" strokeWidth={1.5} />
                    Morning Sessions are currently an experimental feature. You can try up to 3
                    per month. After using one, we'd love to know what you think.
                  </p>
                )}
                <button
                  type="button"
                  onClick={() => choose(t)}
                  disabled={isCurrent || (t.product_id != null && busyProduct === t.product_id)}
                  data-testid={`pricing-choose-${t.id}`}
                  className={`mt-4 w-full rounded-full px-5 py-2.5 text-xs uppercase tracking-[0.2em] transition-soft ${
                    isCurrent
                      ? "border border-violet-200/15 text-violet-300/45 cursor-default"
                      : isPremiumTier
                        ? "border border-amber-300/40 text-amber-200/95 hover:bg-amber-300/10 hover:border-amber-300/60"
                        : "border border-violet-200/30 text-violet-100/90 hover:bg-violet-200/10 hover:border-violet-200/50"
                  }`}
                >
                  {t.product_id != null && busyProduct === t.product_id ? (
                    <span className="inline-flex items-center gap-2">
                      <Loader2 className="w-3 h-3 animate-spin" /> Opening App Store…
                    </span>
                  ) : isCurrent ? "Current plan" : t.id === "free" ? "Included" : `Choose ${t.label.replace("Continua ", "")}`}
                </button>
              </section>
            );
          })}
        </div>

        <button
          type="button"
          onClick={restore}
          disabled={restoring}
          data-testid="pricing-restore-button"
          className="mt-8 w-full text-center text-xs text-violet-300/50 underline underline-offset-4 hover:text-violet-200/80 transition-colors disabled:opacity-50"
        >
          {restoring ? "Restoring…" : "Restore Purchases"}
        </button>
        <p className="mt-4 text-center text-[10px] text-violet-300/35 leading-relaxed">
          Subscriptions renew monthly and are managed through your App Store account.
          Cancel anytime — your plan stays active until the end of the period.
        </p>
      </div>
    </AppShell>
  );
}
